const CHUNK_RELOAD_MARKER = "mint:chunk-load-reload-at";
const CHUNK_RELOAD_COOLDOWN_MS = 60_000;

interface ReloadMarkerStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function errorText(error: unknown): string {
  const parts: string[] = [];
  let current: unknown = error;

  // Browsers and bundlers wrap dynamic-import failures differently. Include a
  // short cause chain so Safari's TypeError wrapper still exposes the useful
  // ChunkLoadError message underneath it.
  for (let depth = 0; depth < 3 && current; depth += 1) {
    if (typeof current === "string") {
      parts.push(current);
      break;
    }
    if (typeof current !== "object") break;

    const value = current as { name?: unknown; message?: unknown; cause?: unknown };
    if (typeof value.name === "string") parts.push(value.name);
    if (typeof value.message === "string") parts.push(value.message);
    current = value.cause;
  }

  return parts.join("\n");
}

/** Dynamic chunks disappear when an already-open tab outlives a deployment. */
export function isChunkLoadError(error: unknown): boolean {
  const text = errorText(error);
  return [
    /ChunkLoadError/i,
    /Loading (?:CSS )?chunk [\w-]+ failed/i,
    /Failed to load chunk/i,
    /Failed to fetch dynamically imported module/i,
    /Importing a module script failed/i,
    /Error loading dynamically imported module/i,
    /CSS_CHUNK_LOAD_FAILED/i,
    /\/_next\/static\/chunks\/[^\s]+\.(?:js|css)/i,
  ].some((pattern) => pattern.test(text));
}

/**
 * Claim one automatic hard reload for this tab.
 *
 * A cooldown is essential: if the deployment really is unhealthy, reloading
 * on every mount would trap the user in an infinite refresh loop. The marker
 * expires so the same long-lived tab can recover from a later deployment too.
 */
export function claimChunkLoadReload(
  error: unknown,
  storage: ReloadMarkerStore,
  now = Date.now(),
): boolean {
  if (!isChunkLoadError(error)) return false;

  try {
    const rawPrevious = storage.getItem(CHUNK_RELOAD_MARKER);
    if (rawPrevious !== null) {
      const previous = Number(rawPrevious);
      if (Number.isFinite(previous) && now - previous < CHUNK_RELOAD_COOLDOWN_MS) {
        return false;
      }
    }
    storage.setItem(CHUNK_RELOAD_MARKER, String(now));
    return true;
  } catch {
    // Without a durable marker we cannot prove that the reload will not loop.
    // Leave the visible retry button in control instead.
    return false;
  }
}
